import React from 'react';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
    Sizes,
    Typography,
    AnchorAnimation,
    getLanguageFromLocale,
} from '@ratehub/base-ui';
import {
    CreditCardShape,
    AnniversaryBonusDescription,
    getSpendingCategoryDescription,
    getCardProductTypeName,
    getCardProductTypeLandingPage,
    getCardNetworkName,
    getCardNetworkLandingPage,
    getCardProviderLandingPage,
    getIsEarningsAsPercentageOverride,
    PRODUCT_TYPES,
} from '@ratehub/cc-common';


function CardDetailsTable({ cardInfo }) {
    const intl = useIntl();
    const language = getLanguageFromLocale(intl.locale);

    /*
        rewardsDescription final string will look like one of:
        Earn <multiplier>x points on <categories>[, <multiplier>x points on <categories>]*
        or
        Earn <percent>% on <categories>[, <percent>% points on <categories>]*
    */
    const isMultiplier = cardInfo.productType !== PRODUCT_TYPES.CASH_BACK
        && !getIsEarningsAsPercentageOverride(cardInfo.id);

    const rewardsDescription = cardInfo.earningsDescription.map(entry => {
        // get descriptions for all the categories listed in this entry’s types array
        const categories = getSpendingCategoryDescription(entry.types, intl);

        return intl.formatMessage(LOCAL_MESSAGES.multiplierOrPercent, {
            isMultiplier: isMultiplier,
            multiplier: entry.rate,
            percent: entry.rate / 100,
        })
            + intl.formatMessage(LOCAL_MESSAGES.onCategories, { categories });
    }).join(', ');

    return (
        // we render a definition list that is formatted as a two-column table
        <MyDefinitionList>
            {/* sign up bonus */}
            <If condition={cardInfo.welcomeBonusDescription || (cardInfo.signUpBonusPoints && cardInfo.signUpBonusDollars)}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.welcome-offer"
                        defaultMessage="Welcome bonus"
                        tagName="dt"
                    />
                    <Choose>
                        <When condition={cardInfo.welcomeBonusDescription}>
                            <dd>
                                {cardInfo.welcomeBonusDescription}
                            </dd>
                        </When>
                        <Otherwise>
                            <FormattedMessage
                                id="cc.details.table.welcome-bonus-points"
                                defaultMessage="{signUpBonusPoints, number} bonus points (a {signUpBonusDollars, number, CAD0} value)"
                                values={cardInfo}
                                tagName="dd"
                            />
                        </Otherwise>
                    </Choose>
                </div>
            </If>

            {/* anniversary bonus */}
            <If condition={cardInfo.anniversaryBonusDescription
                || (cardInfo.anniversaryBonus?.points && cardInfo.anniversaryBonus?.value)}
            >
                <div>
                    <FormattedMessage
                        id="cc.details.table.anniversary-bonus"
                        defaultMessage="Anniversary bonus"
                        tagName="dt"
                    />
                    <dd>
                        <AnniversaryBonusDescription
                            anniversaryBonus={cardInfo.anniversaryBonus}
                            anniversaryBonusDescription={cardInfo.anniversaryBonusDescription}
                        />
                    </dd>
                </div>
            </If>

            {/* annual fee */}
            <If condition={cardInfo.annualFee >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.annual-fee"
                        defaultMessage="Annual fee"
                        tagName="dt"
                    />
                    <FormattedMessage
                        id="cc.details.table.annual-fee-text"
                        defaultMessage={`{annualFee, number, CAD0}
{isFirstYearFeeWaived, select,
    true { (first year waived)}
    other {}}`}
                        values={cardInfo}
                        tagName="dd"
                    />
                </div>
            </If>

            {/* reward points by category */}
            <If condition={rewardsDescription}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.reward"
                        defaultMessage="Reward"
                        tagName="dt"
                    />
                    <dd>
                        <FormattedMessage
                            id="cc.details.table.Earn"
                            defaultMessage="Earn {rewardsDescription}"
                            values={{ rewardsDescription: rewardsDescription }}
                        />
                    </dd>
                </div>
            </If>

            {/* Average point value */}
            <If condition={cardInfo.averagePointValue}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.average-point-value"
                        defaultMessage="Average point value"
                        tagName="dt"
                    />
                    <dd>
                        <FormattedNumber
                            value={cardInfo.averagePointValue}
                            style="currency"
                            currency="CAD"
                            minimumFractionDigits={0}
                            maximumFractionDigits={3}
                        />
                    </dd>
                </div>
            </If>

            {/* Balance transfer offer & duration */}
            <If condition={cardInfo.balanceTransferPromoInterest >= 0
                && cardInfo.balanceTransferPromoMonths > 0}
            >
                <div>
                    <FormattedMessage
                        id="cc.details.table.bt-offer-and-duration"
                        defaultMessage="Balance transfer offer"
                        tagName="dt"
                    />
                    <FormattedMessage
                        id="cc.details.table.balance-transfer-offer"
                        defaultMessage={`{btPromoInterest, number, percent} per month for the first
{balanceTransferPromoMonths, select,
    1 {month}
    other {{balanceTransferPromoMonths, number} months}
}`
                        }
                        values={{
                            btPromoInterest: cardInfo.balanceTransferPromoInterest / 100,
                            balanceTransferPromoMonths: cardInfo.balanceTransferPromoMonths,
                        }}
                        tagName="dd"
                    />
                </div>
            </If>

            {/* interest rates: null is now accepted, and will pass the condition={someRate >= 0} test */}
            <If condition={cardInfo.purchaseInterest >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.purchase-interest"
                        defaultMessage="Purchase interest rate"
                        tagName="dt"
                    />
                    <dd>
                        {/* prevents null or undefined, expecting only the former */}
                        <If condition={cardInfo.purchaseInterest != null}>
                            <FormattedNumber
                                value={cardInfo.purchaseInterest / 100}
                                format="percent2"
                            />
                        </If>
                        {/* luckily N/A is the same in FR as in EN */}
                        <If condition={cardInfo.purchaseInterest == null}>
                            {'N/A'}
                        </If>
                    </dd>
                </div>
            </If>

            <If condition={cardInfo.cashAdvanceInterest >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.cash-advance-interest"
                        defaultMessage="Cash advance interest rate"
                        tagName="dt"
                    />
                    <dd>
                        <If condition={cardInfo.cashAdvanceInterest != null}>
                            <FormattedNumber
                                value={cardInfo.cashAdvanceInterest / 100}
                                format="percent2"
                            />
                        </If>
                        <If condition={cardInfo.cashAdvanceInterest == null}>
                            {'N/A'}
                        </If>
                    </dd>
                </div>
            </If>

            <If condition={cardInfo.balanceTransferInterest >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.balance-transfer-interest"
                        defaultMessage="Balance transfer interest rate"
                        tagName="dt"
                    />
                    <dd>
                        <If condition={cardInfo.balanceTransferInterest != null}>
                            <FormattedNumber
                                value={cardInfo.balanceTransferInterest / 100}
                                format="percent2"
                            />
                        </If>
                        <If condition={cardInfo.balanceTransferInterest == null}>
                            {'N/A'}
                        </If>
                    </dd>
                </div>
            </If>


            {/* minimum incomes */}
            <If condition={cardInfo.minimumIncome >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.minimum-income"
                        defaultMessage="Minimum income"
                        tagName="dt"
                    />
                    <FormattedMessage
                        id="cc.details.table.minimum-income-text"
                        defaultMessage={
                            `{minIncomeThousands, select,
                                0 {Any amount}
                                other {{minIncomeThousands, number, CAD0}K/yr}}`
                        }
                        values={{ minIncomeThousands: cardInfo.minimumIncome / 1000 }}
                        tagName="dd"
                    />
                </div>
            </If>

            <If condition={cardInfo.minimumHouseholdIncome >= 0}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.minimum-household"
                        defaultMessage="Household income"
                        tagName="dt"
                    />
                    <FormattedMessage
                        id="cc.details.table.minimum-household-text"
                        defaultMessage={
                            `{minHouseholdThousands, select,
                                0 {Any amount}
                                other {{minHouseholdThousands, number, CAD0}K/yr}}`
                        }
                        values={{ minHouseholdThousands: cardInfo.minimumHouseholdIncome / 1000 }}
                        tagName="dd"
                    />
                </div>
            </If>


            {/* minimumCreditScore could be null, but not undefined */}
            <If condition={cardInfo.minimumCreditScore != null}>
                <div>
                    <FormattedMessage
                        id="cc.details.table.recommended-credit-score"
                        defaultMessage="Recommended credit score"
                        tagName="dt"
                    />
                    <FormattedMessage
                        id="cc.details.table.recommended-credit-score-text"
                        defaultMessage="{minimumCreditScore, number}+"
                        values={cardInfo}
                        tagName="dd"
                    />
                </div>
            </If>

            <If condition={cardInfo.network}>
                <div>
                    <dt>
                        <FormattedMessage
                            id="cc.details.table.network"
                            defaultMessage="Network"
                        />
                    </dt>
                    <dd>
                        <FormattedMessage
                            id="cc.details.table.network-link"
                            defaultMessage="<anchor>{network}</anchor>"
                            values={{
                                anchor: createAnchorEnhancer(getCardNetworkLandingPage(cardInfo, language)),
                                network: getCardNetworkName(cardInfo, language),
                            }}
                        />
                    </dd>
                </div>
            </If>

            <If condition={cardInfo.bank}>
                <div>
                    <dt>
                        <FormattedMessage
                            id="cc.details.table.provider"
                            defaultMessage="Provider"
                        />
                    </dt>
                    <dd>
                        <FormattedMessage
                            id="cc.details.table.provider-link"
                            defaultMessage="<anchor>{provider}</anchor>"
                            values={{
                                anchor: createAnchorEnhancer(getCardProviderLandingPage(cardInfo, language)),
                                provider: cardInfo.providerName,
                            }}
                        />
                    </dd>
                </div>
            </If>

            <If condition={cardInfo.productType}>
                <div>
                    <dt>
                        <FormattedMessage
                            id="cc.details.table.card-type"
                            defaultMessage="Card type"
                        />
                    </dt>
                    <dd>
                        <FormattedMessage
                            id="cc.details.table.card-type-link"
                            defaultMessage="<anchor>{cardType}</anchor>"
                            values={{
                                anchor: createAnchorEnhancer(
                                    getCardProductTypeLandingPage(cardInfo, language),
                                ),
                                cardType: getCardProductTypeName(cardInfo, language),
                            }}
                        />
                    </dd>
                </div>
            </If>
        </MyDefinitionList>
    );
}


CardDetailsTable.propTypes = {
    cardInfo: CreditCardShape.isRequired,
};


// provides simple parameterization when formatting an <a> within an intl message
function createAnchorEnhancer(url) {
    // eslint-disable-next-line
    return linkText => <a href={url} target="_blank">{linkText}</a>
}


const MyDefinitionList = styled.dl`
    > div {
        display: flex;
        align-items: start;
        line-height: 1.4;
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};

        dt {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            flex: 0 1 35%;
            max-width: 15em;
        }

        dd {
            flex: 1 1 65%;
            margin-left: ${Sizes.SPACING.TWO};

            a {
                ${AnchorAnimation}
            }
        }
    }
`;

const LOCAL_MESSAGES = defineMessages({
    onCategories: {
        id: 'cc.details.table.on-categories',
        defaultMessage: 'on {categories}',
    },

    multiplierOrPercent: {
        id: 'cc.details.table.multiplier-or-percent',
        // \u00A0 is non-breaking space
        // Using {nbsp} instead of \u00A0 and adding DefaultIntlFormatValues above blew intl’s little mind!
        // Suddenly, the number `multiplier` was rendered as [object Object]
        defaultMessage: `{isMultiplier, select, true {{multiplier, number}x\u00A0points }
    other {{percent, number, percent} }
 }`,
    },
});

export default CardDetailsTable;
